import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Leaf, Package } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCedaActive, FAMILY_ACCENTS, euros } from "@/lib/ceda";
import { CapsuleCard } from "@/components/ceda/CapsuleCard";
import { CapsuleSheet } from "@/components/ceda/CapsuleSheet";
import { PackComposer } from "@/components/ceda/PackComposer";
import { PayChoice } from "@/components/ceda/PayChoice";

export default function CedaBoutique() {
  const active = useCedaActive();
  const navigate = useNavigate();
  const { user } = useAuth();
  const authed = !!user && user !== false;
  const [cat, setCat] = useState(null);
  const [family, setFamily] = useState("all");
  const [mode, setMode] = useState("single");
  const [sameFamily, setSameFamily] = useState(false);
  const [selected, setSelected] = useState([]);
  const [sheet, setSheet] = useState(null);
  const [order, setOrder] = useState(null);
  const [owned, setOwned] = useState([]);

  useEffect(() => {
    if (authed && active) {
      api.get("/ceda/me").then((r) => setOwned(r.data.items.filter((i) => i.status !== "expired").map((i) => i.capsule.id))).catch(() => setOwned([]));
    }
  }, [authed, active]);

  useEffect(() => {
    if (active) api.get("/ceda/catalog").then((r) => setCat(r.data)).catch(() => setCat(null));
  }, [active]);

  useEffect(() => {
    if (!authed) return;
    const pending = sessionStorage.getItem("tao_ceda_pending");
    if (pending) { sessionStorage.removeItem("tao_ceda_pending"); setOrder(JSON.parse(pending)); }
  }, [authed]);

  const famById = useMemo(() => Object.fromEntries((cat?.families || []).map((f, i) => [f.id, { ...f, accent: FAMILY_ACCENTS[i % 4] }])), [cat]);
  const capById = useMemo(() => Object.fromEntries((cat?.capsules || []).map((c) => [c.id, c])), [cat]);
  const size = mode === "single" ? 1 : Number(mode);
  const lockedFamily = sameFamily && selected.length ? capById[selected[0]]?.family_id : null;
  const shown = (cat?.capsules || []).filter((c) => (family === "all" || c.family_id === family) && (!lockedFamily || c.family_id === lockedFamily));

  const changeMode = (m) => { setMode(m); setSelected([]); };
  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : s.length < size ? [...s, id] : s));
  const names = (ids) => ids.map((id) => `${capById[id]?.code} — ${capById[id]?.title}`);

  const checkout = (o) => {
    if (!authed) {
      sessionStorage.setItem("tao_ceda_pending", JSON.stringify(o));
      toast("Connectez-vous pour finaliser votre commande");
      navigate("/connexion?next=/ceda");
      return;
    }
    setOrder(o);
  };
  const buySingle = (c) => checkout({ label: "Capsule CEDA", cents: cat.prices.ceda_single, names: names([c.id]), payload: { product: "ceda_single", capsule_ids: [c.id] } });
  const buyComposed = () => checkout({ label: `Pack de ${size} CEDA`, cents: cat.prices[`ceda_pack${size}`], names: names(selected), payload: { product: `ceda_pack${size}`, capsule_ids: selected } });
  const buyFixed = (p) => checkout({ label: p.name, cents: p.price_cents, names: names(p.capsule_ids), payload: { product: `ceda_pack${p.size}`, pack_id: p.id } });

  if (active === null || (active && !cat)) return <p className="max-w-5xl mx-auto px-4 py-24 font-sans text-[#1F2A24]/60">Chargement…</p>;
  if (!active) return <Unavailable />;

  return (
    <div className="pb-16" data-testid="ceda-boutique">
      <CedaHero days={cat.active_days} />
      <div className="max-w-6xl mx-auto px-4 sm:px-8 -mt-10 relative z-10">
        <PackComposer mode={mode} setMode={changeMode} sameFamily={sameFamily} setSameFamily={(v) => { setSameFamily(v); setSelected([]); }}
          selected={selected} size={size} prices={cat.prices} onBuy={buyComposed} onClear={() => setSelected([])} />
      </div>
      <div className="max-w-6xl mx-auto px-4 sm:px-8 mt-12">
        <FamilyTabs families={cat.families} famById={famById} current={family} onChange={setFamily} />
        {shown.length === 0 ? (
          <p className="mt-10 font-sans text-sm text-[#1F2A24]/60" data-testid="ceda-empty">Les capsules de cette famille arrivent bientôt.</p>
        ) : (
          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="ceda-grid">
            {shown.map((c, i) => (
              <CapsuleCard key={c.id} c={c} index={i} accent={famById[c.family_id]?.accent || "#C9A24B"}
                owned={owned.includes(c.id)} price={cat.prices.ceda_single}
                selectable={mode !== "single"} selected={selected.includes(c.id)} disabled={selected.length >= size}
                onToggle={() => toggle(c.id)} onOpen={() => setSheet(c)} onBuy={() => buySingle(c)} />
            ))}
          </div>
        )}
        <FixedPacks packs={cat.packs} capById={capById} onBuy={buyFixed} />
      </div>
      {sheet && <CapsuleSheet c={sheet} family={famById[sheet.family_id]} onClose={() => setSheet(null)} />}
      {order && <PayChoice order={order} onClose={() => setOrder(null)} />}
    </div>
  );
}

const CedaHero = ({ days }) => (
  <section className="bg-[#F8F5EE] border-b border-[#E8DCC4]">
    <div className="max-w-6xl mx-auto px-4 sm:px-8 pt-16 pb-24 md:pt-24 md:pb-32 grid md:grid-cols-[1.4fr_1fr] gap-10 items-end">
      <div className="animate-in fade-in slide-in-from-bottom-4 duration-700">
        <p className="font-ui text-xs tracking-[0.3em] text-[#C9A24B] uppercase inline-flex items-center gap-2"><Leaf size={14} /> Collection CEDA</p>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#1F2A24] mt-4 leading-[1.05]">Compléments Énergétiques<br /><em className="text-[#385E3A]">à Durée Active</em></h1>
        <p className="font-sans text-base text-[#1F2A24]/70 mt-6 max-w-xl">Des capsules guidées pour le Corps, le Cœur, le Cerveau et la Conscience. Une pratique courte, à vivre pendant {days} jours.</p>
      </div>
      <ol className="space-y-3 font-sans text-sm text-[#1F2A24]/75 animate-in fade-in duration-1000">
        {["Choisissez une capsule ou composez un pack", "Elle vous attend dans « Mes CEDA »", `Cliquez sur « Activer » : vos ${days} jours commencent`].map((t, i) => (
          <li key={t} className="flex gap-3"><span className="font-serif text-2xl text-[#C9A24B] leading-none w-6">{i + 1}</span>{t}</li>
        ))}
      </ol>
    </div>
  </section>
);

const FamilyTabs = ({ families, famById, current, onChange }) => (
  <div className="flex flex-wrap gap-2" data-testid="ceda-family-tabs">
    <button data-testid="ceda-family-all" onClick={() => onChange("all")} className={`rounded-full px-5 py-2 font-ui text-sm transition-colors ${current === "all" ? "bg-[#1F2A24] text-white" : "border border-[#E8DCC4] text-[#1F2A24]/70 hover:border-[#1F2A24]/40"}`}>Toutes</button>
    {families.map((f) => (
      <button key={f.id} data-testid={`ceda-family-${f.id}`} onClick={() => onChange(f.id)}
        className={`rounded-full px-5 py-2 font-ui text-sm transition-colors border ${current === f.id ? "text-white" : "text-[#1F2A24]/75 border-[#E8DCC4]"}`}
        style={current === f.id ? { backgroundColor: famById[f.id].accent, borderColor: famById[f.id].accent } : {}}>
        {f.name} <span className="opacity-60 text-xs ml-1">{f.code_range}</span>
      </button>
    ))}
  </div>
);

const FixedPacks = ({ packs, capById, onBuy }) =>
  packs.length > 0 && (
    <section className="mt-16" data-testid="ceda-fixed-packs">
      <h2 className="font-serif text-3xl text-[#1F2A24]">Packs composés pour vous</h2>
      <div className="mt-6 grid md:grid-cols-2 gap-6">
        {packs.map((p) => (
          <div key={p.id} className="rounded-3xl border border-[#C9A24B]/50 bg-white p-6" data-testid={`ceda-pack-${p.id}`}>
            <p className="font-ui text-xs tracking-[0.2em] text-[#C9A24B] uppercase inline-flex items-center gap-1.5"><Package size={13} /> Pack de {p.size}</p>
            <h3 className="font-serif text-2xl text-[#1F2A24] mt-2">{p.name}</h3>
            {p.description && <p className="font-sans text-sm text-[#1F2A24]/70 mt-2">{p.description}</p>}
            <ul className="mt-3 font-sans text-sm text-[#1F2A24]/75 space-y-0.5">
              {p.capsule_ids.map((id) => <li key={id}>· {capById[id]?.code} — {capById[id]?.title}</li>)}
            </ul>
            <button data-testid={`ceda-pack-buy-${p.id}`} onClick={() => onBuy(p)} className="mt-5 rounded-full bg-[#385E3A] text-white px-6 py-3 font-ui text-sm hover:bg-[#2c4a2e] transition-colors">Obtenir · {euros(p.price_cents)}</button>
          </div>
        ))}
      </div>
    </section>
  );

const Unavailable = () => (
  <div className="max-w-lg mx-auto px-4 py-28 text-center" data-testid="ceda-unavailable">
    <Leaf className="mx-auto text-[#C9A24B]" size={32} />
    <h1 className="font-serif text-3xl text-[#1F2A24] mt-4">Page indisponible</h1>
    <p className="font-sans text-sm text-[#1F2A24]/60 mt-2">Cette page n'est pas accessible pour le moment.</p>
  </div>
);
